<template>
  <div class="global-search" :class="{ dark: isDarkMode }">
    <!-- Trigger: icon-only on mobile, labelled pill with shortcut on desktop.
         Ctrl/⌘+K and the 'open-global-search' window event also open it. -->
    <button
      type="button"
      class="search-trigger"
      :class="variant === 'wide' ? 'search-trigger-wide' : 'search-trigger-compact'"
      :aria-label="$t('Search files, projects...') + ' (' + shortcutLabel + ')'"
      :title="$t('Press') + ' ' + shortcutLabel + ' ' + $t('to search')"
      @click="open"
    >
      <i class="fas fa-search" aria-hidden="true"></i>
      <template v-if="variant === 'wide'">
        <span class="trigger-label">{{ $t('Search or jump to...') }}</span>
        <kbd class="trigger-kbd">{{ shortcutLabel }}</kbd>
      </template>
    </button>

    <Teleport to="body">
    <Transition name="gs">
      <div v-if="isOpen" class="search-overlay" :class="{ dark: isDarkMode }" @click.self="close">
        <div class="search-modal" role="dialog" aria-modal="true" :aria-label="$t('Search files, projects...')">
          <div class="search-input-row">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              ref="searchInput"
              v-model="query"
              :placeholder="$t('Search files, projects, transcripts...')"
              :aria-label="$t('Search files, projects, transcripts...')"
              role="combobox"
              aria-autocomplete="list"
              :aria-expanded="flatItems.length > 0 ? 'true' : 'false'"
              aria-controls="gs-results-listbox"
              :aria-activedescendant="flatItems.length > 0 ? 'gs-option-' + selectedIndex : undefined"
              :aria-busy="(loading || aiLoading) ? 'true' : 'false'"
              @input="debouncedSearch"
              @keydown.esc="close"
              @keydown.down.prevent="moveSelection(1)"
              @keydown.up.prevent="moveSelection(-1)"
              @keydown.enter.prevent="selectResult"
            />
            <button v-if="query" type="button" class="search-clear" :aria-label="$t('Clear')" @click="clearQuery">
              <i class="fas fa-xmark" aria-hidden="true"></i>
            </button>
            <kbd class="search-kbd">ESC</kbd>
          </div>

          <!-- Scope filter: narrows which sources are searched -->
          <div class="search-scopes" role="tablist" :aria-label="$t('Search in')">
            <button
              v-for="s in scopes"
              :key="s.id"
              type="button"
              role="tab"
              class="scope-chip"
              :class="{ active: scope === s.id }"
              :aria-selected="scope === s.id ? 'true' : 'false'"
              @click="setScope(s.id)"
            >
              <i :class="['fas', s.icon]" aria-hidden="true"></i>
              {{ $t(s.label) }}
              <span v-if="s.count" class="scope-count">{{ s.count }}</span>
            </button>
          </div>

          <div id="gs-results-listbox" class="search-results" role="listbox" :aria-label="$t('Search results')" :aria-busy="(loading || aiLoading) ? 'true' : 'false'" aria-live="polite">

            <section v-for="section in sections" :key="section.key" class="search-section" :class="'section-' + section.key">
              <div class="search-group-label" :class="{ 'ai-group-label': section.key === 'ai' }">
                <i v-if="section.icon" :class="['fas', section.icon]" aria-hidden="true"></i>
                <span>{{ section.label }}</span>
                <span v-if="section.key === 'ai' && aiLoading" class="ai-loading-dots">…</span>
                <button
                  v-if="section.key === 'recent'"
                  type="button"
                  class="group-action"
                  @click.stop="clearRecentSearches"
                >{{ $t('Clear') }}</button>
              </div>

              <!-- Quick links render as a compact tile grid; everything else as rows -->
              <div :class="section.layout === 'grid' ? 'quick-grid' : 'row-list'">
                <div
                  v-for="entry in section.items"
                  :key="entry.key"
                  :id="'gs-option-' + entry.index"
                  :class="[section.layout === 'grid' ? 'quick-tile' : 'search-result-item', { selected: entry.index === selectedIndex, 'ai-cluster': entry.kind === 'ai' }]"
                  role="option"
                  :aria-selected="entry.index === selectedIndex ? 'true' : 'false'"
                  tabindex="-1"
                  @click="activate(entry)"
                  @mouseenter="selectedIndex = entry.index"
                >
                  <span class="result-icon" :class="entry.tint">
                    <i :class="entry.icon" aria-hidden="true"></i>
                  </span>
                  <span class="result-info">
                    <span class="result-name">{{ entry.name }}</span>
                    <span v-if="entry.meta" class="result-meta" :class="{ 'ai-snippet': entry.kind === 'ai' }">{{ entry.meta }}</span>
                  </span>
                  <template v-if="section.layout !== 'grid'">
                    <button
                      v-if="entry.kind === 'ai' && entry.result.start !== null && entry.result.start !== undefined"
                      class="result-play-btn"
                      :title="$t('Play from here')"
                      :aria-label="$t('Play from here') + ' ' + formatDur(entry.result.start)"
                      @click.stop="playFromResult(entry.result)"
                    >
                      <i class="fas fa-play" aria-hidden="true"></i>
                      <span class="play-time">{{ formatDur(entry.result.start) }}</span>
                    </button>
                    <span v-else-if="entry.badge" class="result-type">{{ entry.badge }}</span>
                    <kbd v-else-if="entry.hotkey" class="result-hotkey">{{ entry.hotkey }}</kbd>
                  </template>
                </div>
              </div>
            </section>

            <div v-if="loading || aiLoading || docsLoading" class="search-skeleton" role="status" aria-live="polite" aria-busy="true" :aria-label="aiLoading ? $t('Searching content...') : $t('Loading results')">
              <div v-if="aiLoading && !loading" class="search-skeleton-label">{{ $t('Searching content...') }}</div>
              <div v-for="n in 2" :key="n" class="skeleton-row">
                <span class="skeleton-bone skeleton-icon"></span>
                <span class="skeleton-lines">
                  <span class="skeleton-bone skeleton-name"></span>
                  <span class="skeleton-bone skeleton-meta"></span>
                </span>
              </div>
            </div>

            <div v-else-if="showNoResults" class="search-empty">
              <span class="search-empty-icon"><i class="fas fa-search" aria-hidden="true"></i></span>
              <p class="search-empty-title">{{ $t('No results found') }}</p>
              <p class="search-empty-hint">{{ $t(scope === 'pages' ? 'Try another page or action name' : 'Search matches file names and transcript content') }}</p>
              <button v-if="isLoggedIn" type="button" class="empty-ai-btn" @click="openAiFromSearch">
                <i class="fas fa-wand-magic-sparkles" aria-hidden="true"></i> {{ $t('Ask AI about') }} “{{ query.trim() }}”
              </button>
            </div>
          </div>

          <div v-if="searchError || aiError" role="alert" class="search-error">
            <span>{{ $t(searchError ? 'File search failed. Please retry.' : 'AI search failed. Please retry.') }}</span>
            <button type="button" @click="debouncedSearch">{{ $t('Retry') }}</button>
          </div>
          <div class="search-footer">
            <span class="footer-hint"><kbd>↑</kbd><kbd>↓</kbd>{{ $t('to navigate') }}</span>
            <span class="footer-hint"><kbd>↵</kbd>{{ $t('to open') }}</span>
            <span class="footer-hint"><kbd>ESC</kbd>{{ $t('to close') }}</span>
            <span v-if="isLoggedIn" class="footer-hint footer-hint-ai">
              <button class="footer-ai-btn" @click="openAiFromSearch">
                <i class="fas fa-wand-magic-sparkles"></i> {{ $t('Ask AI') }}
              </button>
            </span>
          </div>
        </div>
      </div>
    </Transition>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useThemeStore } from '@stores/themeStore';
import { useSessionStore } from '@stores/session';
import { useAiToolsStore } from '@stores/aiToolsStore';
import { useAuthUserStore } from '@stores/auth';
import { useDashboardStore } from '@stores/dashboardStore';
import axios from '@services/axios';
import AutoService from '@services/AutoService';
import { openWithLoading } from '@utils/openWithLoading';
import { buildNavCatalog, itemsInSection, matchCatalog } from '@components/navigation/navCatalog';

// Scan the user's workspaces for the one owning projectId (same pattern as
// TranscriptorDashboard.applyDeepLink). Bounded: first 10 workspaces.
const findWorkspaceForProject = async (workspaces, projectId) => {
  if (!projectId || !Array.isArray(workspaces)) return null;
  for (const ws of workspaces.slice(0, 10)) {
    const wsId = ws.PK?.split('#')[1];
    if (!wsId) continue;
    try {
      const projResp = await AutoService.fetchOrder(`workspaces/${wsId}/projects`);
      const projects = Array.isArray(projResp?.Items) ? projResp.Items : [];
      if (projects.some((pr) => pr.SK === 'PROJECT#' + projectId)) return wsId;
    } catch { /* keep scanning */ }
  }
  return null;
};

const RECENT_KEY = 'gs.recentSearches';
const RECENT_MAX = 5;
const PROJECTS_MAX = 5;

const loadRecent = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((q) => typeof q === 'string').slice(0, RECENT_MAX) : [];
  } catch { return []; }
};
const saveRecent = (list) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* storage unavailable */ }
};

export default defineComponent({
  name: 'GlobalSearch',
  props: {
    // 'compact' = square icon button (mobile), 'wide' = labelled pill (desktop bar)
    variant: { type: String, default: 'compact' },
  },
  setup() {
    const { t } = useI18n();
    const themeStore = useThemeStore();
    const { isDarkMode } = storeToRefs(themeStore);
    const router = useRouter();
    const aiToolsStore = useAiToolsStore();
    const authStore = useAuthUserStore();
    const dashboardStore = useDashboardStore();
    const isProductionRadio = import.meta.env.MODE === 'productionradio';

    const isOpen = ref(false);
    const query = ref('');
    const scope = ref('all');
    const results = ref([]);
    const aiResults = ref([]);
    const aiLoading = ref(false);
    const loading = ref(false);
    const searchError = ref(false), aiError = ref(false);
    const recentSearches = ref(loadRecent());
    // Pas 4.2: Docs index arrives as a lazy chunk on first open (never in
    // the initial bundle). The loaded module's helpers stay behind these
    // refs; until then the Docs section is simply empty.
    const docsLoading = ref(false);
    const docsApi = ref(null);
    let docsLoadPromise = null;
    const isLoggedIn = computed(() => !!authStore.isAuthenticated);
    const loadDocs = () => {
      if (docsApi.value) return;
      if (!docsLoadPromise) {
        docsLoading.value = true;
        docsLoadPromise = import('@utils/docsSearchIndex')
          .then((mod) => { docsApi.value = mod; })
          .catch(() => { /* docs search stays empty; other sources unaffected */ })
          .finally(() => { docsLoading.value = false; });
      }
    };
    let requestVersion = 0, regularAbort, aiAbort;
    const selectedIndex = ref(0);
    const searchInput = ref(null);
    let debounceTimer = null;
    let aiDebounceTimer = null;

    const isMacLike = typeof navigator !== 'undefined'
      && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
    const shortcutLabel = isMacLike ? '⌘K' : 'Ctrl+K';

    // ---------- Destinations (shared with Navigation) ----------
    const roleRoutes = computed(() => {
      const groups = authStore.groups || [];
      return (router.options?.routes || []).filter((r) =>
        r.meta?.requires && r.meta.requires !== 'Users' && groups.includes(r.meta.requires));
    });
    const catalog = computed(() => buildNavCatalog({
      isLoggedIn: isLoggedIn.value,
      isProductionRadio,
      roleRoutes: roleRoutes.value,
      supportEmail: useSessionStore().appConfig?.contactEmail || 'office@transcriptor.ro',
    }));

    // Local-only actions (no navigation) surfaced as quick actions.
    // Unauthenticated visitors get search + guides + theme only: Docs are
    // public, files/AI/account actions are not.
    const actions = computed(() => [
      ...(isLoggedIn.value ? [
        { id: 'new-transcription', label: 'New transcription', icon: 'fa-plus', tint: 'tint-blue', keywords: 'upload transcribe record transcriere noua', run: 'transcribe' },
        { id: 'ask-ai', label: 'Ask AI', icon: 'fa-wand-magic-sparkles', tint: 'tint-violet', keywords: 'assistant question chat intreaba', run: 'ask-ai' },
        ...(isProductionRadio ? [] : [{ id: 'subscription', label: 'Manage subscription', icon: 'fa-coins', tint: 'tint-amber', keywords: 'credits plan upgrade abonament credite', run: 'subscription' }]),
      ] : []),
      { id: 'theme', label: isDarkMode.value ? 'Switch to light mode' : 'Switch to dark mode', icon: isDarkMode.value ? 'fa-sun' : 'fa-moon', tint: 'tint-slate', keywords: 'theme dark light tema', run: 'theme' },
    ]);

    const projectId = (p) => p?.SK?.split('#')[1] || '';
    const recentProjects = computed(() => {
      if (isProductionRadio) return [];
      const seen = new Set();
      const list = [];
      const current = dashboardStore.selectedProject;
      [current, ...(dashboardStore.projects || [])].forEach((p) => {
        const id = projectId(p);
        if (!id || seen.has(id) || list.length >= PROJECTS_MAX) return;
        seen.add(id);
        list.push(p);
      });
      return list;
    });

    // ---------- Entry builders (one shape for every row) ----------
    const navEntry = (item) => ({
      kind: 'nav', key: 'nav-' + item.id, item,
      name: t(item.label),
      icon: 'fas ' + item.icon,
      tint: { workspace: 'tint-blue', account: 'tint-green', admin: 'tint-amber', help: 'tint-slate' }[item.section] || 'tint-slate',
      badge: item.section === 'workspace' ? null : t({ account: 'Account', admin: 'Admin', help: 'Help' }[item.section] || ''),
    });
    const actionEntry = (a) => ({
      kind: 'action', key: 'act-' + a.id, action: a,
      name: t(a.label), icon: 'fas ' + a.icon, tint: a.tint,
      badge: t('Action'),
    });
    const projectEntry = (p) => {
      const isCurrent = projectId(p) === dashboardStore.selectedProjectId;
      return {
        kind: 'project', key: 'proj-' + projectId(p), project: p,
        name: p.name || t('Untitled'),
        meta: [isCurrent ? t('Current project') : null, dashboardStore.selectedWorkspace?.name || null].filter(Boolean).join(' · '),
        icon: 'fas fa-folder', tint: 'tint-blue',
        badge: t('Project'),
      };
    };
    const recentEntry = (q) => ({
      kind: 'recent', key: 'recent-' + q, recentQuery: q,
      name: q, icon: 'fas fa-clock-rotate-left', tint: 'tint-slate',
      hotkey: '↵',
    });
    const fileEntry = (result) => ({
      kind: 'file', key: 'file-' + result.id, result,
      name: result.name, meta: result.meta,
      icon: getResultIcon(result.type).icon, tint: getResultIcon(result.type).tint,
      badge: t(result.type),
    });
    const aiEntry = (cluster, i) => ({
      kind: 'ai', key: 'ai-' + i, result: cluster.primary,
      name: cluster.primary.name, meta: `“${cluster.snippet}”`,
      icon: 'fas fa-wand-magic-sparkles', tint: 'tint-violet',
      badge: t('AI match'),
    });
    // Pas 4.2: a guide hit (or, with no hit, a plain guide row for browsing).
    const docEntry = (hitOrEntry) => {
      const entry = hitOrEntry.entry || hitOrEntry;
      return {
        kind: 'doc', key: 'doc-' + entry.slug + '-' + (entry.anchor || 'top'), docEntryData: entry,
        name: entry.heading ? `${entry.title} — ${entry.heading}` : entry.title,
        meta: hitOrEntry.snippet || '',
        icon: 'fas fa-book-open', tint: 'tint-green',
        badge: t('Guide'),
      };
    };
    const docHits = computed(() => {
      if (!docsApi.value || !hasQuery.value) return [];
      try {
        return docsApi.value.searchGuides(query.value.trim(), scope.value === 'docs' ? 8 : 4);
      } catch { return []; }
    });
    const guideEntries = computed(() => {
      if (!docsApi.value) return [];
      try { return docsApi.value.listGuides(); } catch { return []; }
    });

    const hasQuery = computed(() => query.value.trim().length > 0);
    const wants = (s) => scope.value === 'all' || scope.value === s;

    // ---------- Sections (idle view vs. query view) ----------
    const sections = computed(() => {
      const out = [];
      const push = (key, label, icon, items, layout) => { if (items.length) out.push({ key, label, icon, items, layout }); };

      if (!hasQuery.value) {
        if (scope.value === 'all') {
          push('recent', t('Recent searches'), 'fa-clock-rotate-left', recentSearches.value.map(recentEntry));
          push('projects', t('Recent projects'), 'fa-folder-open', recentProjects.value.map(projectEntry));
        }
        if (wants('pages')) {
          push('goto', t('Go to'), 'fa-compass', itemsInSection(catalog.value, 'workspace').map(navEntry), 'grid');
          push('actions', t('Quick actions'), 'fa-bolt', actions.value.map(actionEntry));
          if (scope.value === 'pages') {
            push('account', t('Account'), 'fa-user', itemsInSection(catalog.value, 'account').map(navEntry));
            push('admin', t('Administration'), 'fa-user-shield', itemsInSection(catalog.value, 'admin').map(navEntry));
            push('help', t('Help & resources'), 'fa-life-ring', itemsInSection(catalog.value, 'help').map(navEntry));
          }
        }
        if (scope.value === 'files' || scope.value === 'content') {
          push('projects', t('Recent projects'), 'fa-folder-open', recentProjects.value.map(projectEntry));
        }
        if (scope.value === 'docs') {
          push('docs', t('User guides'), 'fa-book-open', guideEntries.value.map(docEntry));
        }
      } else {
        const q = query.value.trim();
        if (wants('pages')) {
          const navMatches = matchCatalog(catalog.value, q, t).map(navEntry);
          const actionMatches = matchCatalog(actions.value, q, t).map(actionEntry);
          push('pages', t('Pages & actions'), 'fa-compass', [...navMatches, ...actionMatches].slice(0, 6));
          const lower = q.toLowerCase();
          push('projects', t('Projects'), 'fa-folder-open',
            recentProjects.value.filter((p) => (p.name || '').toLowerCase().includes(lower)).map(projectEntry));
        }
        if (wants('docs')) {
          push('docs', t('User guides'), 'fa-book-open', docHits.value.map(docEntry));
        }
        if (wants('files')) {
          const buckets = [
            { key: 'transcripts', label: t('Transcripts'), icon: 'fa-file-audio', match: (type) => type === 'transcript' },
            { key: 'videos', label: t('Videos'), icon: 'fa-video', match: (type) => type === 'video' },
            { key: 'other', label: t('Other files'), icon: 'fa-file', match: () => true },
          ].map((b) => ({ ...b, items: [] }));
          results.value.forEach((r) => buckets.find((b) => b.match(r.type)).items.push(fileEntry(r)));
          buckets.forEach((b) => push(b.key, b.label, b.icon, b.items));
        }
        if (wants('content')) {
          push('ai', t('Matches in content'), 'fa-wand-magic-sparkles', aiResults.value.map(aiEntry));
        }
      }

      // Assign contiguous indices in render order for keyboard navigation.
      let index = 0;
      out.forEach((section) => { section.items = section.items.map((e) => ({ ...e, index: index++ })); });
      return out;
    });

    const flatItems = computed(() => sections.value.flatMap((s) => s.items));

    const scopes = computed(() => [
      { id: 'all', label: 'All', icon: 'fa-layer-group' },
      ...(isLoggedIn.value ? [
        { id: 'files', label: 'Files', icon: 'fa-file', count: hasQuery.value ? results.value.length : 0 },
        { id: 'content', label: 'Content', icon: 'fa-wand-magic-sparkles', count: hasQuery.value ? aiResults.value.length : 0 },
      ] : []),
      { id: 'docs', label: 'User guides', icon: 'fa-book-open', count: hasQuery.value ? docHits.value.length : 0 },
      { id: 'pages', label: 'Pages', icon: 'fa-compass' },
    ]);

    const showNoResults = computed(() => hasQuery.value
      && !searchError.value && !aiError.value
      && flatItems.value.length === 0
      && (scope.value === 'pages' || query.value.trim().length > 1));

    // ---------- Open / close ----------
    const open = async () => {
      loadDocs(); // Pas 4.2: lazy Docs chunk starts loading on first open.
      recentSearches.value = loadRecent();
      isOpen.value = true;
      selectedIndex.value = 0;
      await nextTick();
      searchInput.value?.focus();
    };

    const resetRemote = () => {
      requestVersion++; regularAbort?.abort(); aiAbort?.abort();
      clearTimeout(debounceTimer); clearTimeout(aiDebounceTimer);
      searchError.value = false; aiError.value = false;
      loading.value = false; aiLoading.value = false;
    };

    const close = () => {
      resetRemote();
      isOpen.value = false;
      query.value = '';
      scope.value = 'all';
      results.value = [];
      aiResults.value = [];
      selectedIndex.value = 0;
    };

    const clearQuery = () => {
      resetRemote();
      query.value = '';
      results.value = [];
      aiResults.value = [];
      selectedIndex.value = 0;
      searchInput.value?.focus();
    };

    const setScope = (id) => {
      scope.value = id;
      selectedIndex.value = 0;
      if (hasQuery.value) debouncedSearch();
      searchInput.value?.focus();
    };

    // ---------- Remote search ----------
    const debouncedSearch = () => {
      resetRemote();
      loadDocs(); // no-op once loaded; covers typing before open() finished
      selectedIndex.value = 0;
      const q = query.value.trim();
      if (q.length < 2) { results.value = []; aiResults.value = []; return; }
      // File/AI sources need an account; Docs search is local and public.
      if (!isLoggedIn.value) { results.value = []; aiResults.value = []; return; }
      if (wants('files')) {
        loading.value = true;
        debounceTimer = setTimeout(() => performSearch(), 300);
      } else {
        results.value = [];
      }
      // AI search is slower (embeddings + clustering) — longer debounce, runs in parallel.
      if (wants('content')) {
        aiLoading.value = true;
        aiDebounceTimer = setTimeout(() => performAiSearch(), 600);
      } else {
        aiResults.value = [];
      }
    };

    const performAiSearch = async () => {
      const version = requestVersion; aiAbort = new AbortController();
      if (query.value.trim().length < 2) return;
      aiLoading.value = true; aiError.value = false;
      try {
        const domain = useSessionStore().appConfig?.domain;
        if (!domain) { aiError.value = true; return; }
        const response = await axios.get(`${domain}api/search/ai`, { params: { q: query.value.trim() }, signal: aiAbort.signal });
        if (version !== requestVersion) return;
        const clusters = response?.data?.clusters || [];
        aiResults.value = clusters.map((cluster) => {
          // Group cluster nodes by element so one file = one row with a snippet.
          const byElement = new Map();
          for (const node of cluster.nodes || []) {
            const eid = node.metadata?.elementId || 'unknown';
            if (!byElement.has(eid)) {
              byElement.set(eid, {
                elementId: eid,
                projectId: node.metadata?.projectId,
                fileName: node.metadata?.fileName || t('Untitled'),
                snippets: [],
                start: null,
              });
            }
            const group = byElement.get(eid);
            if (node.text) group.snippets.push(node.text);
            // Earliest timestamp in the cluster = where playback should start.
            if (typeof node.metadata?.start === 'number'
              && (group.start === null || node.metadata.start < group.start)) {
              group.start = node.metadata.start;
            }
          }
          const groups = [...byElement.values()];
          return {
            primary: {
              id: `ai-${groups[0]?.elementId}-${ci()}`,
              name: groups[0]?.fileName || t('Untitled'),
              type: 'ai-match',
              projectId: groups[0]?.projectId,
              elementId: groups[0]?.elementId,
              elementIds: groups.map((g) => g.elementId),
              start: groups[0]?.start ?? null,
            },
            snippet: truncate(groups.map((g) => g.snippets.join(' … ')).join(' … '), 140),
            nodeCount: (cluster.nodes || []).length,
          };
        });
      } catch {
        if (version !== requestVersion) return;
        aiResults.value = []; aiError.value = true;
      } finally { if (version === requestVersion) aiLoading.value = false; }
    };

    let aiCounter = 0;
    const ci = () => ++aiCounter;

    const truncate = (text, max) =>
      text && text.length > max ? `${text.slice(0, max).trim()}…` : text || '';

    const performSearch = async () => {
      const version = requestVersion; regularAbort = new AbortController(); searchError.value = false;
      if (query.value.trim().length < 2) { results.value = []; loading.value = false; return; }
      loading.value = true;
      try {
        const domain = useSessionStore().appConfig?.domain;
        if (!domain) { searchError.value = true; return; }
        const response = await axios.get(`${domain}api/search`, { params: { q: query.value.trim() }, signal: regularAbort.signal });
        if (version !== requestVersion) return;
        results.value = (response?.data?.Items || []).map(item => ({
          id: item.elementId || item.PK,
          name: item.fileName || t('Untitled'),
          type: item.type || 'file',
          projectId: item.projectId,
          pk: item.PK,
          meta: [
            item.language ? item.language.toUpperCase() : null,
            item.duration ? formatDur(item.duration) : null,
            item.createdAt ? formatWhen(item.createdAt) : null,
          ].filter(Boolean).join(' · '),
        }));
      } catch {
        if (version !== requestVersion) return;
        results.value = []; searchError.value = true;
      } finally {
        if (version === requestVersion) loading.value = false;
      }
    };

    const formatDur = (seconds) => {
      const s = Number(seconds) || 0;
      const m = Math.floor(s / 60);
      return m > 0 ? `${m}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`;
    };

    const formatWhen = (iso) => {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return null;
      const days = Math.floor((Date.now() - date.getTime()) / 86400000);
      if (days <= 0) return t('Today');
      if (days === 1) return t('Yesterday');
      if (days < 7) return `${days} ${t('days ago')}`;
      return date.toLocaleDateString();
    };

    // ---------- Recent searches ----------
    const rememberQuery = () => {
      const q = query.value.trim();
      if (q.length < 2) return;
      const next = [q, ...recentSearches.value.filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(0, RECENT_MAX);
      recentSearches.value = next;
      saveRecent(next);
    };
    const clearRecentSearches = () => {
      recentSearches.value = [];
      saveRecent([]);
      selectedIndex.value = 0;
      searchInput.value?.focus();
    };

    // ---------- Selection ----------
    const moveSelection = (dir) => {
      const count = flatItems.value.length;
      if (!count) return;
      // Wrap around so ↑ on the first row reaches the last one.
      selectedIndex.value = (selectedIndex.value + dir + count) % count;
      nextTick(() => {
        document.getElementById('gs-option-' + selectedIndex.value)?.scrollIntoView({ block: 'nearest' });
      });
    };

    const selectResult = () => {
      const entry = flatItems.value[selectedIndex.value];
      if (entry) activate(entry);
    };

    const runNav = (item) => {
      close();
      if (item.href) { window.location.href = item.href; return; }
      if (item.action === 'subscription') { window.dispatchEvent(new Event('open-subscription-modal')); return; }
      if (item.id === 'transcribe') { goTranscribe(); return; }
      const target = String(item.route || item.path);
      const loc = target.startsWith('/') ? { path: target } : { name: target };
      router.push(loc).catch(() => {});
    };

    // Transcribe resumes the selected workspace/project (same as the nav bar).
    const goTranscribe = () => openWithLoading(router, async () => {
      try { await dashboardStore.ensureDefaultSelection(); } catch { /* fall through to picker */ }
      const wsId = dashboardStore.selectedWorkspaceId;
      const projId = dashboardStore.selectedProjectId;
      const q = wsId && projId ? { workspace: wsId, project: projId } : undefined;
      return { name: 'TranscriptEditorDashboard', query: q };
    });

    const runAction = (a) => {
      if (a.run === 'ask-ai') { openAiFromSearch(); return; }
      if (a.run === 'theme') { themeStore.toggleTheme(); return; }
      close();
      if (a.run === 'transcribe') goTranscribe();
      else if (a.run === 'subscription') window.dispatchEvent(new Event('open-subscription-modal'));
    };

    const openProject = (p) => {
      close();
      const wsId = dashboardStore.selectedWorkspaceId;
      openWithLoading(router, { name: 'TranscriptEditorDashboard', query: { workspace: wsId || undefined, project: projectId(p) } }, p?.name || '');
    };

    const activate = (entry) => {
      if (entry.kind === 'recent') {
        query.value = entry.recentQuery;
        debouncedSearch();
        searchInput.value?.focus();
        return;
      }
      rememberQuery();
      if (entry.kind === 'nav') runNav(entry.item);
      else if (entry.kind === 'action') runAction(entry.action);
      else if (entry.kind === 'project') openProject(entry.project);
      else if (entry.kind === 'doc') openDoc(entry.docEntryData);
      else navigateToResult(entry.result);
    };

    // Pas 4.2: guide result → /docs/:slug#ancora-heading.
    const openDoc = (doc) => {
      close();
      router.push({
        name: 'DocGuide',
        params: { slug: doc.slug },
        hash: doc.anchor ? `#${doc.anchor}` : undefined,
      }).catch(() => {});
    };

    const routeForType = (type) =>
      type === 'video' ? '/Users/videodashboard' : '/Users/transcriptordashboard';

    const navigateToResult = (result) => {
      close();
      if (result.type === 'ai-match') { router.push({ name: 'SavedTranscripts', query: { transcriptId: result.elementId } }); return; }
      const path = routeForType(result.type);
      if (path === '/Users/videodashboard') {
        router.push({ path });
        return;
      }
      if (!result.projectId) {
        router.push({ path: '/Users/dashboard' });
        return;
      }
      // Resolve the owning workspace first: the transcriptordashboard deep
      // link only applies workspace+project when the workspace id is present —
      // without it applyDeepLink scans up to 10 workspaces and may land wrong.
      const goWithWorkspace = (workspaceId) => {
        const deepLink = { project: result.projectId, workspace: workspaceId };
        const inputId = result.pk && result.pk.startsWith('INPUT#') ? result.pk.split('#')[1] : null;
        if (inputId) deepLink.input = inputId;
        router.push({ path, query: deepLink });
      };
      AutoService.fetchOrder('workspaces').then((wsResp) => {
        const workspaces = wsResp?.Items || [];
        findWorkspaceForProject(workspaces, result.projectId).then((wsId) => {
          goWithWorkspace(wsId || undefined);
        }).catch(() => goWithWorkspace(undefined));
      }).catch(() => goWithWorkspace(undefined));
    };

    // Play button on AI results: same navigation but with autoplay intent.
    const playFromResult = (result) => {
      rememberQuery();
      close();
      router.push({ name: 'SavedTranscripts', query: { transcriptId: result.elementId, time: String(result.start || 0) } });
    };

    const getResultIcon = (type) => {
      if (type === 'transcript') return { icon: 'fas fa-file-audio', tint: 'tint-blue' };
      if (type === 'video') return { icon: 'fas fa-video', tint: 'tint-red' };
      if (type === 'translation') return { icon: 'fas fa-language', tint: 'tint-green' };
      return { icon: 'fas fa-file', tint: 'tint-slate' };
    };

    const openAiFromSearch = () => {
      const q = query.value.trim();
      rememberQuery();
      close();
      aiToolsStore.openAsk({ scope: 'global', question: q.length >= 2 ? q : '' });
    };

    const handleKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        isOpen.value ? close() : open();
      } else if (e.key === 'Escape' && isOpen.value) {
        close();
      }
    };

    onMounted(() => {
      window.addEventListener('keydown', handleKeydown);
      window.addEventListener('open-global-search', open);
    });
    onUnmounted(() => {
      close();
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('open-global-search', open);
    });

    return {
      searchError, aiError,
      isDarkMode, isOpen, isLoggedIn, query, scope, scopes, aiLoading, loading, docsLoading, selectedIndex, searchInput,
      shortcutLabel, sections, flatItems, showNoResults,
      open, close, clearQuery, setScope, debouncedSearch, moveSelection, selectResult, activate,
      playFromResult, openAiFromSearch, clearRecentSearches, formatDur,
    };
  },
});
</script>

<style scoped>
.global-search {
  --gs-display-font: var(--adm-font-display, 'Sora', sans-serif);
  --gs-body-font: var(--font-body, 'IBM Plex Sans', sans-serif);
  --gs-mono-font: var(--adm-font-mono, 'IBM Plex Mono', monospace);

  margin: 0 12px;
  flex: none;
  display: flex;
  align-items: center;
  font-family: 'IBM Plex Sans', sans-serif;
}

/* ---------- Trigger (sits on the brand-blue navbar) ---------- */
.search-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.88);
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}
.search-trigger:hover {
  background: rgba(255, 255, 255, 0.24);
  border-color: rgba(255, 255, 255, 0.55);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(10, 15, 44, 0.18);
}
.search-trigger:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.search-trigger > i { font-size: 0.8rem; opacity: 0.9; }
.search-trigger-compact {
  width: 38px;
  height: 38px;
  padding: 0;
  border-radius: 10px;
  justify-content: center;
  flex: none;
}
.search-trigger-compact > i { font-size: 0.9rem; opacity: 1; }

/* ---------- Overlay + modal ---------- */
/* ---------- Overlay + modal ----------
   Self-contained theme tokens because this element is teleported to <body>
   (outside .global-search DOM scope) — same fix pattern as Navigation's .sheet-backdrop.
   Light tokens below; .dark override follows. */
.search-overlay {
  /* light — canonical --adm-* with fallbacks (visual appearance preserved) */
  --gs-ink: var(--adm-text-strong, #0a0f2c);
  --gs-brand: var(--adm-accent, #003cff);
  --gs-brand-soft: var(--adm-accent-soft, #e6ecff);
  --gs-signal: var(--adm-success, #00e5a0);
  --gs-paper: var(--adm-bg, #f5f7fc);
  --gs-panel: var(--adm-card, #ffffff);
  --gs-border: var(--adm-border, #e4e8f5);
  --gs-text: var(--adm-text, #3a4160);
  --gs-muted: var(--adm-muted, #8a92b8);
  --gs-row-active: var(--adm-accent-soft, #e6ecff);
  --gs-display-font: var(--adm-font-display, 'Sora', sans-serif);
  --gs-body-font: var(--font-body, 'IBM Plex Sans', sans-serif);
  --gs-mono-font: var(--adm-font-mono, 'IBM Plex Mono', monospace);

  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 15vh 16px 0;
  background: rgba(6, 9, 26, 0.62);
}
.search-overlay.dark,
html.dark-mode .search-overlay {
  /* dark overrides — canonical --adm-* with fallbacks */
  --gs-ink: var(--adm-text-strong, #e6e9f7);
  --gs-brand: var(--adm-accent, #4d72ff);
  --gs-brand-soft: var(--adm-accent-soft, #1a2250);
  --gs-paper: var(--adm-bg, #0a0f2c);
  --gs-panel: var(--adm-card, #141b3f);
  --gs-border: var(--adm-border, #26305e);
  --gs-text: var(--adm-text, #e6e9f7);
  --gs-muted: var(--adm-muted, #8a92b8);
  --gs-row-active: var(--adm-card-head, #1c2650);
}
.search-modal {
  width: 100%;
  max-width: 640px;
  background: var(--gs-panel);
  border: 1px solid var(--gs-border);
  border-radius: 12px;
  box-shadow: 0 24px 70px rgba(4, 7, 20, 0.45);
  overflow: hidden;
}
.search-modal::before {
  content: '';
  display: block;
  height: 3px;
  background: linear-gradient(90deg, var(--gs-brand), var(--gs-signal));
}

.search-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--gs-border);
}
.search-input-row > i { color: var(--gs-muted); font-size: 0.95rem; }
.search-input-row input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 1rem;
  color: var(--gs-ink);
}
.search-input-row input::placeholder { color: var(--gs-muted); }
.search-kbd {
  flex: none;
  font-family: var(--gs-mono-font);
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  color: var(--gs-muted);
  background: var(--gs-paper);
  border: 1px solid var(--gs-border);
  border-bottom-width: 2px;
  border-radius: 5px;
  padding: 3px 7px;
}

/* ---------- Results ---------- */
.search-results {
  max-height: min(440px, 58vh);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px;
}
.search-group-label {
  padding: 10px 12px 4px;
  font-family: var(--gs-mono-font);
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--gs-muted);
}
.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}
.search-result-item:hover,
.search-result-item.selected {
  background: var(--gs-row-active);
  box-shadow: inset 2px 0 0 0 var(--gs-brand);
}
.search-result-item:focus-visible {
  outline: 2px solid var(--gs-brand);
  outline-offset: -2px;
  background: var(--gs-row-active);
}
.result-icon {
  flex: none;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  font-size: 0.85rem;
}
.tint-blue { background: #e6ecff; color: #003cff; }
.tint-red { background: #ffe9ec; color: #e5484d; }
.tint-green { background: #def7ef; color: #0b9e6f; }
.tint-slate { background: #edf0f7; color: #64748b; }
.tint-violet { background: #f1e9ff; color: #7c3aed; }
.dark .tint-blue { background: rgba(77, 114, 255, 0.18); color: #8fa6ff; }
.dark .tint-red { background: rgba(229, 72, 77, 0.16); color: #ff9296; }
.dark .tint-green { background: rgba(0, 229, 160, 0.13); color: #00e5a0; }
.dark .tint-slate { background: rgba(138, 146, 184, 0.15); color: #aab3da; }
.dark .tint-violet { background: rgba(139, 92, 246, 0.16); color: #c4b5fd; }
.result-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.result-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--gs-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-meta {
  font-family: var(--gs-mono-font);
  font-size: 0.68rem;
  color: var(--gs-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.result-type {
  flex: none;
  font-family: var(--gs-mono-font);
  font-size: 0.6rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gs-muted);
  background: var(--gs-paper);
  border: 1px solid var(--gs-border);
  border-radius: 5px;
  padding: 3px 7px;
}
.ai-group-label { color: #7c3aed; display: flex; align-items: center; gap: 6px; }
.dark .ai-group-label { color: #c4b5fd; }
.ai-loading-dots { animation: ai-pulse 1.2s ease-in-out infinite; }
@keyframes ai-pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
.ai-snippet {
  white-space: normal !important;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.result-play-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(124, 58, 237, 0.35);
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
  border-radius: 6px;
  padding: 4px 9px;
  cursor: pointer;
  font-family: var(--gs-mono-font);
  font-size: 0.68rem;
  transition: background 0.15s ease, transform 0.1s ease;
}
.result-play-btn:hover { background: rgba(124, 58, 237, 0.18); transform: scale(1.04); }
.result-play-btn .play-time { font-weight: 600; }
.dark .result-play-btn { border-color: rgba(196, 181, 253, 0.4); background: rgba(139, 92, 246, 0.14); color: #c4b5fd; }
.dark .result-play-btn:hover { background: rgba(139, 92, 246, 0.28); }

/* ---------- Loading skeleton ---------- */
.search-skeleton { display: flex; flex-direction: column; gap: 4px; padding: 6px 4px; }
.skeleton-row { display: flex; align-items: center; gap: 12px; padding: 7px 12px; }
.skeleton-bone {
  border-radius: 6px;
  background: linear-gradient(90deg, var(--gs-paper) 25%, var(--gs-border) 50%, var(--gs-paper) 75%);
  background-size: 200% 100%;
  animation: gs-shimmer 1.3s linear infinite;
}
.skeleton-icon { flex: none; width: 32px; height: 32px; border-radius: 8px; }
.skeleton-lines { flex: 1; display: flex; flex-direction: column; gap: 6px; }
.skeleton-name { height: 10px; width: 52%; }
.skeleton-meta { height: 8px; width: 72%; }
.skeleton-row:nth-child(2) .skeleton-name { width: 40%; }
.skeleton-row:nth-child(2) .skeleton-meta { width: 56%; }
.skeleton-row:nth-child(3) .skeleton-name { width: 61%; }
.skeleton-row:nth-child(3) .skeleton-meta { width: 47%; }
@keyframes gs-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

/* ---------- Empty + hint states ---------- */
.search-empty { text-align: center; padding: 34px 20px 30px; }
.search-empty-icon {
  width: 46px;
  height: 46px;
  margin: 0 auto 12px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--gs-paper);
  border: 1px solid var(--gs-border);
  color: var(--gs-muted);
  font-size: 0.95rem;
}
.search-empty-title {
  margin: 0;
  font-family: var(--gs-display-font);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--gs-ink);
}
.search-empty-hint {
  margin: 6px 0 0;
  font-family: var(--gs-mono-font);
  font-size: 0.68rem;
  color: var(--gs-muted);
}
.search-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 26px 20px 24px;
  text-align: center;
}
.search-hint-text { margin: 0; font-size: 0.82rem; color: var(--gs-muted); }

/* ---------- Footer ---------- */
.search-footer {
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid var(--gs-border);
  background: var(--gs-paper);
  font-family: var(--gs-mono-font);
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  color: var(--gs-muted);
  flex-wrap: wrap;
  align-items: center;
}
.footer-hint { display: inline-flex; align-items: center; gap: 4px; }
.footer-hint-ai { margin-left: auto; }
.footer-ai-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(124, 58, 237, 0.3);
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  font-family: var(--gs-mono-font);
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  transition: background 0.15s;
}
.footer-ai-btn:hover { background: rgba(124, 58, 237, 0.16); }
.dark .footer-ai-btn { border-color: rgba(196,181,253,0.35); background: rgba(139,92,246,0.12); color: #c4b5fd; }
.search-footer kbd {
  font-family: inherit;
  color: var(--gs-text);
  background: var(--gs-panel);
  border: 1px solid var(--gs-border);
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 1px 5px;
}

/* ---------- Transitions ---------- */
.gs-enter-active,
.gs-leave-active { transition: opacity 0.17s ease; }
.gs-enter-active .search-modal,
.gs-leave-active .search-modal {
  transition: transform 0.17s cubic-bezier(0.22, 0.9, 0.3, 1), opacity 0.17s ease;
}
.gs-enter-from,
.gs-leave-to { opacity: 0; }
.gs-enter-from .search-modal,
.gs-leave-to .search-modal {
  transform: translateY(-10px) scale(0.98);
  opacity: 0.5;
}

/* ---------- Mobile ---------- */
@media (max-width: 640px) {
  .search-overlay { padding: 10vh 4vw 0; }
  .search-modal { max-width: 92vw; }
  .search-result-item { min-height: 48px; }
}

/* ---------- Wide trigger (desktop bar) ---------- */
.search-trigger-wide {
  width: clamp(180px, 22vw, 280px);
  height: 36px;
  justify-content: flex-start;
}
.trigger-label {
  flex: 1;
  min-width: 0;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.trigger-kbd {
  flex: none;
  font-family: var(--gs-mono-font);
  font-size: 0.64rem;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 5px;
  border: 1px solid currentColor;
  opacity: 0.7;
}

/* ---------- Clear button + scopes ---------- */
.search-clear {
  flex: none;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: var(--gs-paper);
  color: var(--gs-muted);
  cursor: pointer;
}
.search-clear:hover { color: var(--gs-ink); }
.search-scopes {
  display: flex;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--gs-border);
  overflow-x: auto;
  scrollbar-width: none;
}
.search-scopes::-webkit-scrollbar { display: none; }
.scope-chip {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid var(--gs-border);
  background: transparent;
  color: var(--gs-text);
  font-family: var(--gs-body-font);
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.scope-chip i { font-size: 0.7rem; color: var(--gs-muted); }
.scope-chip:hover { border-color: var(--gs-brand); color: var(--gs-brand); }
.scope-chip.active {
  background: var(--gs-brand-soft);
  border-color: var(--gs-brand);
  color: var(--gs-brand);
  font-weight: 600;
}
.scope-chip.active i { color: var(--gs-brand); }
.scope-count {
  font-family: var(--gs-mono-font);
  font-size: 0.62rem;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--gs-paper);
  color: var(--gs-muted);
}
.scope-chip:focus-visible { outline: 2px solid var(--gs-brand); outline-offset: 1px; }

/* ---------- Sections ---------- */
.search-section + .search-section { margin-top: 4px; }
.search-group-label { display: flex; align-items: center; gap: 6px; }
.search-group-label > i { font-size: 0.62rem; }
.group-action {
  margin-left: auto;
  border: none;
  background: none;
  padding: 0;
  color: var(--gs-muted);
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
}
.group-action:hover { color: var(--gs-brand); }
.row-list { display: flex; flex-direction: column; }

/* Quick-link tiles */
.quick-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 6px;
  padding: 2px 4px 4px;
}
.quick-tile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid var(--gs-border);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.quick-tile .result-icon { width: 28px; height: 28px; font-size: 0.8rem; }
.quick-tile .result-name { font-size: 0.84rem; font-weight: 500; }
.quick-tile:hover,
.quick-tile.selected {
  background: var(--gs-row-active);
  border-color: var(--gs-brand);
}
.result-hotkey {
  flex: none;
  font-family: var(--gs-mono-font);
  font-size: 0.62rem;
  color: var(--gs-muted);
  opacity: 0;
}
.search-result-item.selected .result-hotkey { opacity: 1; }
.tint-amber { background: #fff4d6; color: #b7791f; }
.dark .tint-amber { background: rgba(255, 184, 0, 0.14); color: #ffcf5c; }

/* ---------- Errors + empty CTA ---------- */
.search-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid var(--gs-border);
  font-size: 0.8rem;
  color: #d92d20;
}
.search-error button {
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  border-radius: 6px;
  padding: 3px 10px;
  cursor: pointer;
}
.search-skeleton-label {
  padding: 4px 12px 0;
  font-family: var(--gs-mono-font);
  font-size: 0.66rem;
  color: var(--gs-muted);
}
.empty-ai-btn {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  border: 1px solid rgba(124, 58, 237, 0.3);
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark .empty-ai-btn { border-color: rgba(196,181,253,0.35); background: rgba(139,92,246,0.12); color: #c4b5fd; }

@media (max-width: 640px) {
  .quick-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .search-footer .footer-hint:not(.footer-hint-ai) { display: none; }
}
</style>
